/**
 * Copy shared by every nomination page. Per-person text lives in `nominees/*.ts`.
 * Tokens in braces are filled at render time by `fillCopy`.
 */
export const communityCopy = {
  topBarEyebrow: 'By nomination only',
  heroEyebrow: 'A private nomination',
  heroTitle: '{nomineeFirst}, {nominatorFirst} put {mark}your name{/mark} forward.',
  heroLead:
    'A closed room for firm owners is being put together, and a seat in it is being held for you. There is nothing to buy and nothing to apply for. Only a yes or a no.',
  cardEyebrow: 'Nominee',
  cardPutForwardBy: 'Put forward by',
  roomEyebrow: 'The room',
  roomTitle: 'A closed room for people who run firms.',
  roomBody: [
    'We are building a closed, invite-only community for modern firm owners in tax, accounting, and bookkeeping. Real conversations between people running real firms. No vendors, no selling, no audience.',
    'Small rooms, seated with peers. Never with your competitors down the road.',
  ],
  principles: [
    {
      number: '01',
      title: 'Owners only',
      body: 'Every seat is held by someone who runs a firm. No vendors, no consultants, no observers.',
    },
    {
      number: '02',
      title: 'Small rooms',
      body: 'Small enough that everyone talks and nobody performs.',
    },
    {
      number: '03',
      title: 'Nothing for sale',
      body: 'No pitches and no sponsors. What is said in the room stays in the room.',
    },
    {
      number: '04',
      title: 'Never your competitor',
      body: 'Rooms are seated so you sit with peers, never with the firm down the road.',
    },
  ],
  reviewerStatement: '{reviewerFirst} is building the room and is holding your seat.',
  reviewerSub: 'Leave the best way to reach you and he will get in touch. No application, no sales call, no one else in between.',
  form: {
    eyebrow: 'Your seat',
    title: 'Consider yourself in.',
    emailLabel: 'Where should {reviewerFirst} reach you?',
    emailPlaceholder: 'you@yourfirm.com',
    topicLabel: 'One thing a room of firm owners should actually talk about?',
    optional: 'optional',
    submit: 'Confirm my seat',
    submitting: 'Sending',
    finePrint: 'Takes ten seconds. Declining is fine too. The nomination stays between us.',
    decline: 'Not for me right now',
    declineRetry: 'Try declining again',
    declineErrorBody: "We couldn't confirm your response was saved. Please try again or email {reviewerFirst} at {reviewerEmail}.",
    declinedTitle: 'Understood.',
    declinedBody: 'Nothing more will be sent, and the nomination stays between us.',
    acceptedTitle: 'Thank you, {nomineeFirst}.',
    acceptedBody: 'Your seat is held. {reviewerFirst} will be in touch at {email}.',
    errorBody: 'Something went wrong on our side. Email {reviewerFirst} directly at {reviewerEmail} and the nomination stands.',
  },
  footerNote: 'This page was made for {nomineeName}. It is private and not indexed.',
  footerBrand: 'United Accountants',
} as const

export type CopyTokens = Record<string, string | number>

/** Replaces `{token}` placeholders. Unknown tokens are left as-is so gaps stay visible in review. */
export const fillCopy = (template: string, tokens: CopyTokens) =>
  template.replace(/\{(\w+)\}/g, (match, key: string) => (key in tokens ? String(tokens[key]) : match))

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/** `2026-09-08` -> `September 2026`. String-based so server and client render identically. */
export const formatNominationMonth = (isoDate: string) => {
  const [year, month] = isoDate.split('-')
  const monthIndex = Number(month) - 1
  return `${monthNames[monthIndex] ?? ''} ${year}`.trim()
}

/** `42` -> `Nº 042`. */
export const formatNominationNumber = (value: number) => `Nº ${String(value).padStart(3, '0')}`
